import Link from 'next/link';
import { useRouter } from 'next/router';
import { FC, useState } from 'react';
import { useDoctorProfile } from 'reactQuery/queries';
import { useMenus } from 'reactQuery/queries/useMenus';
import { Styled } from './';
import {
  delayTimeInMillisecond,
  isTabActive,
  onClickHandler,
  onNavbarItemMouseEnter,
  onNavbarItemMouseOut,
  onNavbarMouseEnter,
  onNavbarMouseOut,
  onParentCategoryItemHover,
  shouldShowMenuItem,
} from './Navigation.helper';
import { useI18n } from 'contexts/i18n';

const Navigation: FC = () => {
  const [submenuValue, setSubmenuValue] = useState(true);
  const [hoveredMenuId, setHoveredMenuId] = useState<string | null>(null);
  const [dynamicSubmenuBackgroundHeight, setDynamicSubmenuBackgroundHeight] =
    useState<number | undefined>(0);

  const { data: menus, isLoading, isError } = useMenus();
  const { data: doctorProfile } = useDoctorProfile();

  const mainMenuItems = menus?.children;
  const i18n = useI18n();
  const router = useRouter();

  // when Loading, this component preoccupies space for the actual menu, and makes the height constant
  if (isLoading) {
    return (
      <>
        <Styled.Navbar>
          <Styled.NavbarItem>
            <Styled.ParentCategoryItem style={{ visibility: 'hidden' }}>
              {/* non breaking space added to trigger height rendering */}
              <div>&nbsp;</div>
            </Styled.ParentCategoryItem>
          </Styled.NavbarItem>
        </Styled.Navbar>
      </>
    );
  }

  if (isError) {
    return <></>;
  }

  return (
    <>
      <Styled.Navbar
        isThereSubmenu={submenuValue}
        delayTimeInMillisecond={delayTimeInMillisecond}
        dynamicSubmenuBackgroundHeight={dynamicSubmenuBackgroundHeight}
      >
        {mainMenuItems?.map(mainMenu => {
          if (shouldShowMenuItem(mainMenu, doctorProfile?.country)) {
            const isActive = isTabActive(router, mainMenu?.link);
            const isHovered = hoveredMenuId === mainMenu.id;
            const hasChildren =
              mainMenu.children && mainMenu.children.length > 0;

            return (
              <Styled.NavbarItem
                key={mainMenu.id}
                isThereSubmenu={submenuValue}
                delayTimeInMillisecond={delayTimeInMillisecond}
                onMouseEnter={() => {
                  onNavbarItemMouseEnter(setSubmenuValue, mainMenu);
                  setHoveredMenuId(mainMenu.id);
                }}
                onMouseLeave={() => {
                  setHoveredMenuId(null);
                }}
              >
                <Link href={mainMenu.link} passHref legacyBehavior>
                  <Styled.ParentCategoryItem
                    className={`parentNav 
                    ${isActive && 'active'} 
                    ${isHovered && 'hovered'}`}
                    onMouseOver={(event: React.MouseEvent<HTMLDivElement>) =>
                      onParentCategoryItemHover(
                        event,
                        setDynamicSubmenuBackgroundHeight
                      )
                    }
                  >
                    {mainMenu.link === '/customcase' && (
                      <Styled.NewItem>40% {i18n['off']}</Styled.NewItem>
                    )}
                    {mainMenu.link === '/subscribe-and-save' && (
                      <Styled.NewItem>{i18n['New']}</Styled.NewItem>
                    )}
                    {mainMenu.name}
                  </Styled.ParentCategoryItem>
                </Link>

                {hasChildren && (
                  <Styled.SubCategoryContainer
                    className="subNav"
                    isHovered={isHovered}
                    onMouseEnter={() => {
                      setHoveredMenuId(mainMenu.id);
                    }}
                    onMouseLeave={() => {
                      setHoveredMenuId(null);
                    }}
                  >
                    <Styled.SubCategoryList>
                      {mainMenu.children?.map(subMenu => (
                        <Link
                          key={subMenu.id}
                          href={subMenu.link ? subMenu.link : ''}
                          passHref
                          legacyBehavior
                        >
                          <Styled.SubCategoryListItem>
                            {subMenu.name}
                          </Styled.SubCategoryListItem>
                        </Link>
                      ))}
                    </Styled.SubCategoryList>
                  </Styled.SubCategoryContainer>
                )}
              </Styled.NavbarItem>
            );
          }
        })}
      </Styled.Navbar>
    </>
  );
};

export default Navigation;
